<template>
	<div
		class="app-root"
		:class="{ 'app-root--nav': showGlobalNav, 'app-root--fullscreen': !showGlobalNav }"
	>
		<CustomNavBar
			v-if="showGlobalNav"
			:title="navTitle"
			:show-badge="navShowBadge"
			:badge-text="navBadgeText"
			@back="onNavBack"
		/>
		<div class="app-main">
			<router-view />
		</div>
	</div>
</template>
<script>
	import CustomNavBar from '@/components/CustomNavBar.vue'
	import { syncSafeAreaInsetTop, watchSafeAreaInsetTop } from '@/utils/safeArea'

	export default {
		components: { CustomNavBar },
		data() {
			return {
				_unwatchSafeArea: null,
				/** 由 webview1 首屏 URL 带 showAppNav=1 触发，之后所有路由统一显/隐顶栏 */
				_webviewNavEnabled: false,
			}
		},
		created() {
			try {
				if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('wkd_show_app_nav') === '1') {
					this._webviewNavEnabled = true
				}
			} catch (e) {}
			this.absorbShowAppNavQuery(this.$route)
		},
		watch: {
			$route(to) {
				this.absorbShowAppNavQuery(to)
			},
		},
		mounted() {
			syncSafeAreaInsetTop()
			this._unwatchSafeArea = watchSafeAreaInsetTop()
			/* hash 路由首帧 $route.query 可能尚未带上 showAppNav，再扫一次地址栏 */
			this.$nextTick(() => {
				this.absorbShowAppNavQuery(this.$route)
				syncSafeAreaInsetTop()
			})
		},
		beforeDestroy() {
			if (typeof this._unwatchSafeArea === 'function') {
				this._unwatchSafeArea()
			}
		},
		computed: {
			/** 有 showAppNav=1（来自 webview1 写死）即显示；不再按安卓过滤，避免 iOS WebView / 模拟器不显示 */
			showGlobalNav() {
				return this._webviewNavEnabled
			},
			navTitle() {
				return this.$route.meta.navTitle || '万口得'
			},
			navShowBadge() {
				return this.$route.meta.navBadge === true
			},
			navBadgeText() {
				return this.$route.meta.navBadgeText || '100'
			}
		},
		methods: {
			/** 从路由 query 与地址栏（#/path?a=1）读取 showAppNav，避免 hash 首帧 query 为空 */
			absorbShowAppNavQuery(route) {
				if (this._readShowAppNavFromRoute(route)) {
					this._setWebviewNavEnabled()
					return
				}
				if (typeof window !== 'undefined' && this._readShowAppNavFromLocation()) {
					this._setWebviewNavEnabled()
				}
			},
			_readShowAppNavFromRoute(route) {
				const q = (route && route.query) || {}
				return this._isShowAppNavTruthy(q.showAppNav)
			},
			_readShowAppNavFromLocation() {
				try {
					const hash = window.location.hash || ''
					const qi = hash.indexOf('?')
					if (qi !== -1) {
						const hp = new URLSearchParams(hash.slice(qi + 1))
						if (this._isShowAppNavTruthy(hp.get('showAppNav'))) return true
					}
					const sp = new URLSearchParams(window.location.search || '')
					if (this._isShowAppNavTruthy(sp.get('showAppNav'))) return true
				} catch (e) {}
				return false
			},
			_isShowAppNavTruthy(flag) {
				return (
					flag === '1' ||
					flag === 'true' ||
					flag === true ||
					flag === 1
				)
			},
			_setWebviewNavEnabled() {
				this._webviewNavEnabled = true
				try {
					if (typeof sessionStorage !== 'undefined') {
						sessionStorage.setItem('wkd_show_app_nav', '1')
					}
				} catch (e) {}
			},
			onNavBack() {
				/* 嵌入 App web-view：uni.postMessage；需等 UniAppJSBridgeReady（index.html 已引 uni-webview-js） */
				if (this.$route.meta.navBack !== 'uni') {
					this.fallbackBack()
					return
				}
				const send = () => {
					try {
						if (typeof uni !== 'undefined' && uni.postMessage) {
							uni.postMessage({
								data: { action: 'navigateBack' },
							})
						}
					} catch (e) {
						this.fallbackBack()
					}
				}
				if (typeof uni !== 'undefined' && uni.postMessage) {
					send()
				} else {
					document.addEventListener('UniAppJSBridgeReady', send, { once: true })
				}
			},
			fallbackBack() {
				if (window.history.length > 1) {
					this.$router.go(-1)
				} else {
					this.$router.replace('/index')
				}
			}
		}
	}
</script>

<style>
	html,
	body {
		height: 100%;
	}
	body{
		margin: 0;
		touch-action: pan-x pan-y !important;
		/* 禁止双指缩放 */
		-webkit-user-drag: none !important;
		/* 禁止双指拖曳 */
		
		/* IOS禁止微信调整字体大小 */
		 -webkit-text-size-adjust: 100% !important;
		 text-size-adjust: 100% !important;
		 -moz-text-size-adjust: 100% !important;
	}
	.app-root {
		min-height: 100%;
		min-height: 100vh;
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		/*
		 * 导航条内容区用固定 px，避免 max/min/vw 嵌套在 calc 里被部分 WebView 判无效，
		 * 导致 padding-top 整条失效、页面铺满盖住固定导航。
		 */
		--app-nav-inner-h: 48px;
		--app-nav-offset: 0px;
	}

	.app-main {
		position: relative;
		z-index: 0;
		flex: 1;
		min-height: 0;
		width: 100%;
	}

	/* hideNav 为 true：无顶栏，内容区铺满视口（仍可用页面内自己处理刘海） */
	.app-root--fullscreen {
		--app-nav-offset: 0px;
		padding: 0;
		min-height: 100vh;
		min-height: 100dvh;
	}
	.app-root--fullscreen .app-main {
		flex: 1;
		min-height: 100vh;
		min-height: 100dvh;
		width: 100%;
	}

	/* 与 CustomNavBar 一致：安全区 + 内条高度，避免内容顶到刘海下沿或被导航挡住 */
	.app-root--nav {
		/* --app-safe-area-top 由 src/utils/safeArea.js 写入，应对 WebView 内 env 为 0 */
		--app-nav-offset: calc(var(--app-safe-area-top, 0px) + var(--app-nav-inner-h));
		padding-top: calc(var(--app-safe-area-top, 0px) + var(--app-nav-inner-h));
		padding-left: constant(safe-area-inset-left);
		padding-left: env(safe-area-inset-left, 0px);
		padding-right: constant(safe-area-inset-right);
		padding-right: env(safe-area-inset-right, 0px);
		padding-bottom: constant(safe-area-inset-bottom);
		padding-bottom: env(safe-area-inset-bottom, 0px);
	}
</style>
